import {
  BLACK_BALL,
  CUE_BALL,
  DEFAULT_SINUCA_RULES,
  ballOnTurn,
  otherPlayer,
  valueOf,
  type PlayerIndex,
  type SinucaEnding,
  type SinucaFoul,
  type SinucaOutcome,
  type SinucaRuleSet,
  type SinucaRuling,
  type SinucaState,
} from './types'

/**
 * Regras da sinuca brasileira.
 *
 * Funções PURAS, como no 8-Ball: mesmo par (estado, tacada) sempre produz o
 * mesmo julgamento, para servidor e cliente não discordarem.
 *
 * O que torna este jogo diferente do 8-Ball, e que o código precisa refletir:
 *
 *   - Não há grupos. Os dois disputam a MESMA sequência, da menor para a maior.
 *   - Pontua-se por bola, e a partida se decide no PLACAR — não em encaçapar
 *     uma bola final.
 *   - Falta dá pontos ao adversário, não só passa a vez.
 *   - Bola encaçapada indevidamente VOLTA para a mesa. No 8-Ball, só a 8 volta.
 */

export function judgeSinucaShot(
  state: SinucaState,
  outcome: SinucaOutcome,
  rules: SinucaRuleSet = DEFAULT_SINUCA_RULES,
): SinucaRuling {
  if (state.winner !== null) throw new Error('A partida já terminou.')

  const jogador = state.turn
  const adversario = otherPlayer(jogador)

  const alvo = targetBall(state, rules)
  const falta = detectarFalta(state, outcome, alvo)
  const caidas = coloridasForaDeJogo(outcome)

  // Falta anula os pontos e devolve tudo à mesa; sem falta, sai só o alvo.
  const alvoCaiu = !falta && alvo !== null && outcome.pocketed.includes(alvo)

  const scored = alvoCaiu ? valueOf(alvo) : 0
  const penalty = falta ? rules.foulPoints : 0
  const respot = falta ? caidas : caidas.filter((id) => id !== alvo)

  const placar: [number, number] = [...state.score]
  placar[jogador] += scored
  placar[adversario] += penalty

  const naMesa = alvoCaiu ? state.onTable.filter((id) => id !== alvo) : state.onTable

  const fim = fimDePartida(placar, naMesa, rules)

  return {
    foul: falta,
    scored,
    penalty,
    continues: alvoCaiu && !fim.respotBlack,
    // A preta da decisão entra no respot para a MESA física repô-la também: sem
    // isto a partida continua nas regras com uma bola que não existe no feltro.
    respot: fim.respotBlack && !respot.includes(BLACK_BALL) ? [...respot, BLACK_BALL] : respot,
    ...fim,
  }
}

/**
 * Qual bola esta tacada é obrigada a acertar primeiro.
 *
 * Normalmente é a menor da mesa. Se o jogador declarou bola livre — direito
 * que nasce de ter encaçapado a da vez —, o alvo passa a ser a declarada.
 *
 * EXPORTADA de propósito: a interface precisa do mesmo número que o julgamento
 * cobra. Enquanto o `mode.ts` refazia esta conta por fora, o destaque na mesa
 * mostrava a nomeada e o texto do placar mostrava a menor — e quem acreditava
 * no texto mirava errado e levava a penalidade.
 */
export function targetBall(
  state: SinucaState,
  rules: SinucaRuleSet = DEFAULT_SINUCA_RULES,
): number | null {
  if (rules.freeBallAfterPot && nominacaoValida(state, state.nominated)) return state.nominated
  return ballOnTurn(state)
}

/**
 * A bola livre declarada é declarável?
 *
 * A nomeação vem do CLIENTE, e sem esta checagem uma bola inexistente virava
 * alvo obrigatório: toda tacada dos dois jogadores era `free-ball-missed`, 7
 * pontos ao adversário, e nenhum dos dois tinha tacada legal disponível para
 * sempre. A branca e a própria bola da vez também não valem — a bola livre é,
 * por definição, OUTRA bola da mesa.
 */
export function nominacaoValida(state: SinucaState, id: number | null): id is number {
  if (id === null || id === CUE_BALL) return false
  if (!state.onTable.includes(id)) return false
  return id !== ballOnTurn(state)
}

function detectarFalta(
  state: SinucaState,
  outcome: SinucaOutcome,
  alvo: number | null,
): SinucaFoul | null {
  if (outcome.pocketed.includes(CUE_BALL)) return 'cue-ball-pocketed'
  if (outcome.offTable.includes(CUE_BALL)) return 'cue-ball-off-table'
  if (outcome.offTable.some((id) => id !== CUE_BALL)) return 'ball-off-table'

  if (outcome.firstContact === null) return 'no-contact'

  if (alvo !== null && outcome.firstContact !== alvo) {
    // Errar a bola LIVRE tem nome próprio: o jogador escolheu correr o risco,
    // e a mensagem precisa deixar isso claro em vez de dizer "bola errada".
    return nominacaoValida(state, state.nominated) ? 'free-ball-missed' : 'wrong-ball-first'
  }

  // Bola encaçapada que não era o alvo é falta. Só devolvê-la à mesa não pune
  // nada: acertar a da vez e derrubar a preta junto sairia melhor que acertar
  // limpo, e são 7 dos 28 pontos da partida por ocorrência.
  if (outcome.pocketed.some((id) => id !== CUE_BALL && id !== alvo)) {
    return 'wrong-ball-potted'
  }

  if (outcome.pocketed.length === 0 && !outcome.railAfterContact) {
    return 'no-rail-after-contact'
  }

  return null
}

/**
 * Coloridas que saíram de jogo nesta tacada, encaçapadas OU saltadas fora.
 *
 * As saltadas contam: na sinuca nada some da mesa, e sem elas aqui a bola
 * pulava para fora fisicamente mas continuava em `onTable`. Se fosse a menor,
 * `ballOnTurn` apontava para ela para sempre e nenhum dos dois jogadores
 * conseguia fazer uma tacada legal de novo.
 */
const coloridasForaDeJogo = (outcome: SinucaOutcome): number[] =>
  [...outcome.pocketed, ...outcome.offTable].filter((id) => id !== CUE_BALL)

type FimDePartida = {
  winner: PlayerIndex | null
  ending: SinucaEnding | null
  respotBlack: boolean
}

/**
 * A partida acabou?
 *
 * Três saídas: acabaram as bolas, a diferença de pontos passou do que ainda dá
 * para marcar, ou acabaram as bolas EMPATADO — e aí a partida não acaba.
 *
 * O empate é o caso que não pode existir num jogo a dinheiro. Antes, a mesa
 * vazia com 14x14 devolvia `winner: null` junto de um `ending` preenchido: o
 * servidor parava de aceitar tacadas, o escrow ficava preso e nenhum dos dois
 * jogadores era elegível a receber. A saída provável era anular e reembolsar —
 * exatamente o free-roll que `standingWinner` existe para fechar.
 *
 * A regra escolhida é a da sinuca de verdade: repõe-se a bola de MAIOR valor e
 * disputa-se a decisão nela. É a que menos surpreende quem está jogando por
 * dinheiro, porque é o desfecho que ele já viu numa mesa de bar, e porque a
 * partida continua sendo decidida JOGANDO, não por um critério de secretaria.
 * O desempate administrativo (`perdeEmpate`) fica reservado para quando não há
 * mais como jogar — ver `standingWinner` em `mode.ts`.
 *
 * A decisão sempre resolve: com só a preta na mesa, encaçapá-la vale 7 e uma
 * falta vale 7, e qualquer um dos dois desfaz a igualdade.
 */
function fimDePartida(
  placar: [number, number],
  naMesa: number[],
  rules: SinucaRuleSet,
): FimDePartida {
  const empatado = placar[0] === placar[1]

  if (naMesa.length === 0) {
    if (empatado) return { winner: null, ending: null, respotBlack: true }
    return { winner: vencedorPor(placar), ending: { kind: 'all-balls-potted' }, respotBlack: false }
  }

  if (rules.endOnUnreachableScore) {
    const disponivel = naMesa.reduce((soma, id) => soma + valueOf(id), 0)
    if (Math.abs(placar[0] - placar[1]) > disponivel) {
      return {
        winner: vencedorPor(placar),
        ending: { kind: 'score-unreachable' },
        respotBlack: false,
      }
    }
  }

  return { winner: null, ending: null, respotBlack: false }
}

/**
 * Quem venceu pelo placar.
 *
 * Só é chamada onde o empate já foi tratado: uma partida encerrada sem vencedor
 * é dinheiro sem destinatário.
 */
function vencedorPor(placar: [number, number]): PlayerIndex {
  return placar[0] > placar[1] ? 0 : 1
}

/**
 * Aplica o julgamento e devolve o estado da próxima tacada.
 *
 * Separado do julgamento porque a interface precisa MOSTRAR o que aconteceu
 * antes de avançar a vez.
 */
export function applySinucaRuling(
  state: SinucaState,
  outcome: SinucaOutcome,
  ruling: SinucaRuling,
  rules: SinucaRuleSet = DEFAULT_SINUCA_RULES,
): SinucaState {
  const jogador = state.turn
  const adversario = otherPlayer(jogador)

  const score: [number, number] = [...state.score]
  score[jogador] += ruling.scored
  score[adversario] += ruling.penalty

  // Sai da mesa só o que foi encaçapado legalmente; o resto volta ao lugar.
  const removidas = outcome.pocketed.filter(
    (id) => id !== CUE_BALL && !ruling.respot.includes(id),
  )

  const restaram = state.onTable.filter((id) => !removidas.includes(id))

  /*
   * A decisão na preta: a mesa esvaziou empatada, a bola de maior valor volta.
   *
   * Precisa ser reconstruído aqui e não só filtrado porque a preta pode ter sido
   * encaçapada TACADAS antes — nesse caso ela não está em `onTable` nem em
   * `removidas`, e filtrar não a traria de volta.
   */
  const onTable = ruling.respotBlack ? [BLACK_BALL] : restaram

  const proximo = ruling.continues ? jogador : adversario

  return {
    /*
     * Quem joga a decisão é quem NÃO quebrou, com a branca na mão.
     *
     * A sinuca de verdade sorteia; sortear aqui quebraria o replay, que precisa
     * ser reproduzível. "Quem não quebrou" é fixo, conhecido pelos dois desde
     * antes da primeira tacada, e compensa a vantagem de ter aberto a mesa —
     * o mesmo critério de `perdeEmpate`, por coerência.
     */
    turn: ruling.respotBlack ? otherPlayer(state.breaker) : proximo,
    score,
    onTable,
    // O direito à bola livre nasce de encaçapar a da vez e vale só a próxima.
    // A nomeação é validada contra a mesa QUE FICOU: uma bola que acabou de sair
    // não é declarável, e aceitar cru o que o cliente mandou travava a vez.
    nominated:
      ruling.continues && rules.freeBallAfterPot &&
      nominacaoValida({ ...state, onTable }, outcome.nominated)
        ? outcome.nominated
        : null,
    /*
     * Bola na mão só quando a BRANCA SAIU DE JOGO.
     *
     * A CBBS diz "retornando ao jogo como 'na mão'" — a expressão pressupõe
     * que ela saiu. Numa falta comum, como errar a bola da vez, joga-se de
     * onde a branca parou; o castigo é os 7 pontos, não a mesa inteira.
     *
     * Dar bola na mão em toda falta era a maior distorção de equilíbrio da
     * modalidade, e tinha um efeito colateral concreto: 28 posições gravadas
     * em 32 tacadas. O teto de posicionamentos do replay estourava antes de
     * quase toda partida terminar.
     */
    ballInHand:
      ruling.respotBlack ||
      outcome.pocketed.includes(CUE_BALL) ||
      outcome.offTable.includes(CUE_BALL),
    broken: true,
    breaker: state.breaker,
    winner: ruling.winner,
    ending: ruling.ending,
  }
}

export function playSinucaShot(
  state: SinucaState,
  outcome: SinucaOutcome,
  rules: SinucaRuleSet = DEFAULT_SINUCA_RULES,
): { state: SinucaState; ruling: SinucaRuling } {
  const ruling = judgeSinucaShot(state, outcome, rules)
  return { state: applySinucaRuling(state, outcome, ruling, rules), ruling }
}

export function forfeitSinuca(state: SinucaState, quemDesiste: PlayerIndex): SinucaState {
  return { ...state, winner: otherPlayer(quemDesiste), ending: { kind: 'forfeit' } }
}
